import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import useFormatter from 'src/customHooks/useFormatter';
import { NearestExpiringContract } from 'src/viewModels/Organization';
import StatusIndicator, { Status } from '../shared/StatusIndicator';
import { Box } from '@gmg/gmg-react-components';
import { addDaysToDate } from 'src/shared/dateManipulator';


interface ValidityCellProps {
    contractInfo: NearestExpiringContract | undefined;
}

interface Details {
    status: Status;
    statusLabel: string;
}

type UseCase = 'expired' | 'willAutoRenew' | 'willExpire' | 'willExpireSoon' | 'undefinedExpirationDate' | 'expiredWithGracePeriod';

const ValidityCell: FunctionComponent<ValidityCellProps> = ({ contractInfo }) => {
    const { t } = useTranslation();
    const { formatDate } = useFormatter();

    // If there is no contract, we don't show the row in the table at all, so nearestExpiringContract should always be defined. This is only a fallback.
    if (!contractInfo) return <span></span>;

    const { hasExpired, expirationDate, hasAutoRenewal } = contractInfo;

    const formattedExpirationDate = formatDate(expirationDate);

    const useCaseToDetails = new Map<UseCase, Details>([
        ['expired', {
            status: 'error',
            statusLabel: t('ProductManagementTable.validity_column.expired_desc', {
                defaultValue: 'Expired on {{date}}',
                date: formattedExpirationDate,
            }),
        }],
        ['willAutoRenew', {
            status: 'success',
            statusLabel: t('ProductManagementTable.validity_column.will_autorenew_desc', {
                defaultValue: 'Auto renews on {{date}}',
                date: formattedExpirationDate,
            }),
        }],
        ['willExpire', {
            status: 'success',
            statusLabel: t('ProductManagementTable.validity_column.will_expire_desc', {
                defaultValue: 'Expires on {{date}}',
                date: formattedExpirationDate,
            }),
        }],
        ['willExpireSoon', {
            status: 'warning',
            statusLabel: t('ProductManagementTable.validity_column.will_expire_desc', {
                defaultValue: 'Expires on {{date}}',
                date: formattedExpirationDate,
            }),
        }],
        ['undefinedExpirationDate', {
            status: 'success',
            statusLabel: t('ProductManagementTable.validity_column.undefined_expiration_date_desc', 'Umlimited'),
        }],
        ['expiredWithGracePeriod', {
            status: 'warning',
            statusLabel: t('ProductManagementTable.validity_column.expired_desc', {
                defaultValue: 'Expired on {{date}}',
                date: formattedExpirationDate,
            }),
        }],
    ]);

    function getUseCase(): UseCase {
        if (expirationDate === undefined) return 'undefinedExpirationDate';

        const today = new Date();
        const in30Days = addDaysToDate(today, 30);
        const expirationDateWithGracePeriod = addDaysToDate(new Date(expirationDate), 15);
        const isContractWithinGracePeriod = expirationDateWithGracePeriod.getTime() > today.getTime();

        if (hasExpired && hasAutoRenewal && isContractWithinGracePeriod) return 'expiredWithGracePeriod';
        if (hasExpired) return 'expired';
        if (hasAutoRenewal) return 'willAutoRenew';
        if (new Date(expirationDate) < in30Days) return 'willExpireSoon';

        return 'willExpire';
    };

    const details = useCaseToDetails.get(getUseCase())!;

    return (
        <>
            <StatusIndicator statusClass={details.status} />
            <Box aria-label='status-label'ml="12px">{details.statusLabel}</Box>
        </>
    );
};

export default ValidityCell;